import { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Share2, Copy, Navigation } from 'lucide-react';

import { usePropertyStore } from '@/stores/property-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { useLogEntryStore } from '@/stores/log-entry-store';
import { PropertyMap } from '@/components/map/PropertyMap';
import { WaypointList } from '@/components/property/WaypointList';
import { TypePickerSheet } from '@/components/waypoint/TypePickerSheet';
import { ShareManager } from '@/components/sharing/ShareManager';
import { PropertyInsightsPanel } from '@/components/patterns/PropertyInsightsPanel';
import { Breadcrumb } from '@/components/common/Breadcrumb';
import { Button } from '@/components/common/Button';
import { DataCockpit } from '@/components/common/DataCockpit';
import { getStateName } from '@/utils/us-states';
import { COVERED_STATES } from '@/services/gmu-service';
import { hasMultiYearScoutingData } from '@/utils/comparison-utils';
import type { WaypointType } from '@/types/waypoint';

function hasCoords(lat: number, lng: number): boolean {
  return lat !== 0 || lng !== 0;
}

export default function PropertyDetailPage() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();

  const { properties, isLoading, loadProperties, removeProperty, setPrimary } = usePropertyStore();
  const { waypoints, loadWaypoints } = useWaypointStore();
  const { entries, loadEntriesByProperty } = useLogEntryStore();

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSettingPrimary, setIsSettingPrimary] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showShareManager, setShowShareManager] = useState(false);
  const [coordsCopied, setCoordsCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (properties.length === 0) {
      void loadProperties();
    }
  }, [properties.length, loadProperties]);

  useEffect(() => {
    if (propertyId) void loadEntriesByProperty(propertyId);
  }, [propertyId, loadEntriesByProperty]);

  useEffect(() => {
    if (propertyId) void loadWaypoints(propertyId);
  }, [propertyId, loadWaypoints]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-body-sm text-[var(--color-text-muted)]">Loading…</p>
      </div>
    );
  }

  const property = properties.find((p) => p.id === propertyId);

  if (!property) {
    return (
      <div className="flex flex-col gap-4 animate-fade-in">
        <p className="text-body-sm text-[var(--color-text-secondary)]">Property not found.</p>
        <Link
          to="/properties"
          className="text-body-sm text-[var(--color-accent)] hover:opacity-80 transition-opacity"
        >
          ← Back to Properties
        </Link>
      </div>
    );
  }

  async function handleDelete() {
    if (!property) return;
    setIsDeleting(true);
    try {
      await removeProperty(property.id);
      void navigate('/properties', { replace: true });
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleSetPrimary() {
    if (!property) return;
    setIsSettingPrimary(true);
    try {
      await setPrimary(property.id);
    } catch {
      // Error already logged in the store
    } finally {
      setIsSettingPrimary(false);
    }
  }

  function handleAddLocationFromList() {
    setSheetOpen(true);
  }

  function handleTypeSelected(type: WaypointType) {
    setSheetOpen(false);
    void navigate(`/land/properties/${property.id}/waypoints/new?type=${type}`);
  }

  function handleCopyCoords() {
    if (!property) return;
    const coordsString = `${property.latitude.toFixed(6)}, ${property.longitude.toFixed(6)}`;
    void navigator.clipboard.writeText(coordsString).then(() => {
      setCoordsCopied(true);
      copyTimerRef.current = setTimeout(() => setCoordsCopied(false), 2000);
    }).catch(() => {
      // fallback: execCommand for older/restricted browsers
      const el = document.createElement('textarea');
      el.value = coordsString;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCoordsCopied(true);
      copyTimerRef.current = setTimeout(() => setCoordsCopied(false), 2000);
    });
  }

  function handleOpenDirections() {
    if (!property) return;
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${property.latitude},${property.longitude}`,
      '_blank',
    );
  }

  const coords = hasCoords(property.latitude, property.longitude);
  const hasMultiYearData = hasMultiYearScoutingData(entries);

  // ── Build GMU display string ──────────────────────────────────────────────
  function getGmuValue(): string {
    if (!property.state || !COVERED_STATES.has(property.state)) return '—';
    if (property.gmuStatus === 'pending') return 'Resolving…';
    if (property.gmuStatus === 'resolved' && property.gmu) return property.gmu.display;
    if (property.gmuStatus === 'no_coordinates') return 'Set coords';
    if (property.gmuStatus === 'outside_coverage') return 'Outside area';
    if (property.gmuStatus === 'error') return 'Error';
    return '—';
  }

  const cockpitCells = [
    {
      primary: property.state ? getStateName(property.state) : '—',
      label: 'STATE',
      testId: 'cockpit-state',
    },
    {
      primary: getGmuValue(),
      label: 'GAME UNIT',
      testId: 'cockpit-gmu',
    },
    {
      primary: property.zone ? property.zone.replace(/_/g, ' ') : '—',
      label: 'ZONE',
      testId: 'cockpit-zone',
    },
    {
      primary: coords
        ? `${property.latitude.toFixed(4)}, ${property.longitude.toFixed(4)}`
        : 'Not set',
      label: 'COORDINATES',
      testId: 'cockpit-coordinates',
    },
  ];

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      {/* Breadcrumb + header */}
      <div className="flex flex-col gap-1">
        <Breadcrumb label="Properties" to="/properties" />
        <div className="flex items-center gap-2 mt-1">
          <h1
            className="font-display text-display-md text-[var(--color-text-display)] flex-1 min-w-0 truncate"
            data-variant="display"
          >
            {property.name}
          </h1>
          {/* M-07 Primary badge — mirrors SeasonDetailPage pattern (RULE-088: copied inline). */}
          {property.isPrimary && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-body-xs font-medium bg-[var(--color-accent-subtle)] text-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/30 flex-shrink-0"
              data-testid="primary-badge"
            >
              Primary
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mt-1">
          <Button
            variant="ghost"
            onClick={() => setShowShareManager((v) => !v)}
            className="flex items-center gap-1.5"
            data-testid="share-button"
          >
            <Share2 size={14} />
            Share
          </Button>
          <Button
            variant="ghost"
            onClick={() => void navigate(`/land/properties/${property.id}/edit`)}
            className="flex items-center gap-1.5"
          >
            <Pencil size={14} />
            Edit
          </Button>
          {/* M-07 Set as Primary toggle — only shown when 2+ properties exist
              and this property is not already primary (mirrors SeasonDetailPage). */}
          {properties.length >= 2 && !property.isPrimary && (
            <Button
              variant="ghost"
              onClick={() => void handleSetPrimary()}
              disabled={isSettingPrimary}
              data-testid="property-set-primary"
              data-primary-toggle-available="true"
            >
              {isSettingPrimary ? 'Setting…' : 'Set as Primary'}
            </Button>
          )}
          <Button
            variant="danger"
            onClick={() => setShowConfirmDelete(true)}
            className="flex items-center gap-1.5"
          >
            <Trash2 size={14} />
            Delete
          </Button>
        </div>
      </div>

      {/* Property status plate — 2×2 DataCockpit */}
      <section
        className="border-[0.5px] border-[var(--color-border)] rounded-lg shadow-[var(--shadow-card)] overflow-hidden"
        data-testid="property-status-plate"
      >
        <DataCockpit cells={cockpitCells} />
        {/* Coordinates action row — copy + directions */}
        {coords && (
          <div className="bg-[var(--color-surface)] border-t border-[var(--color-border)] px-3 py-2 flex items-center gap-3">
            <span className="text-body-xs text-[var(--color-text-muted)] flex-1">
              {property.latitude.toFixed(6)}, {property.longitude.toFixed(6)}
            </span>
            <button
              type="button"
              onClick={handleCopyCoords}
              className="p-2 cursor-pointer text-[var(--color-text-placeholder)] hover:text-[var(--color-accent)] active:text-[var(--color-accent)] transition-colors"
              aria-label={coordsCopied ? 'Copied!' : 'Copy coordinates'}
              title={coordsCopied ? 'Copied!' : 'Copy coordinates'}
              data-testid="copy-coords-button"
            >
              {coordsCopied ? (
                <span className="text-body-xs text-[var(--color-accent)] font-medium">Copied!</span>
              ) : (
                <Copy size={16} />
              )}
            </button>
            <button
              type="button"
              onClick={handleOpenDirections}
              className="p-2 cursor-pointer text-[var(--color-text-placeholder)] hover:text-[var(--color-accent)] active:text-[var(--color-accent)] transition-colors"
              aria-label="Get directions"
              title="Get directions"
            >
              <Navigation size={16} />
            </button>
          </div>
        )}
        {property.notes && (
          <div className="bg-[var(--color-surface)] border-t border-[var(--color-border)] px-3 py-2">
            <p className="text-body-sm text-[var(--color-text-secondary)] leading-relaxed">
              {property.notes}
            </p>
          </div>
        )}
      </section>

      {/* Share manager panel */}
      {showShareManager && (
        <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-[var(--shadow-card)] p-4 sm:p-5">
          <ShareManager propertyId={property.id} propertyName={property.name} />
        </section>
      )}

      {/* Embedded map — satellite view with all overlay toggles */}
      <section
        className="border-[0.5px] border-[#2d2a26] shadow-card-md rounded-lg overflow-hidden h-48"
        data-testid="mini-map-lens"
      >
        <PropertyMap
          property={property}
          locations={waypoints.filter((wp) => wp.visibleOnMap !== false)}
          defaultTileLayer="satellite"
          requireTapToInteract={true}
          hiddenOverlays={['boundary']}
        />
      </section>

      {/* Waypoints list */}
      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-[var(--shadow-card)] p-4 sm:p-5">
        <WaypointList
          propertyId={property.id}
          onAddWaypoint={handleAddLocationFromList}
        />
      </section>

      {/* Property Insights panel */}
      <PropertyInsightsPanel propertyId={property.id} propertyName={property.name} />

      {/* Compare button — only shown when scouting entries span 2+ calendar years */}
      {hasMultiYearData && (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => void navigate(`/properties/${property.id}/compare`)}
            data-testid="compare-button"
          >
            Compare Years
          </Button>
          <span className="text-body-xs text-[var(--color-text-muted)]">
            View year-over-year scouting data
          </span>
        </div>
      )}

      {/* TypePickerSheet — only mount on explicit "Add Location" tap */}
      {sheetOpen && (
        <TypePickerSheet
          isOpen={sheetOpen}
          onTypeSelected={handleTypeSelected}
          onClose={() => setSheetOpen(false)}
        />
      )}

      {/* Delete confirmation dialog */}
      {showConfirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-delete-title"
        >
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-6 max-w-sm w-full shadow-lg">
            <h2
              id="confirm-delete-title"
              className="font-display text-display-sm text-[var(--color-text-display)] mb-2"
            >
              Delete &ldquo;{property.name}&rdquo;?
            </h2>
            <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
              This will permanently delete the property and all its locations, tasks, and scouting
              log entries. This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                onClick={() => setShowConfirmDelete(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => void handleDelete()}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Delete property'}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
